import React from 'react';
import { Box, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useProfile } from '../context/ProfileContext';

// Compact orange "Profile Loaded" banner shown above the inputs in the calculators.
// Reads the active selection from ProfileContext and lists each member as
// "Name · Age X · Income $Y/mo · Other $Z/yr". Renders nothing when no profile is selected.
const fmtIncome = (value: number): string =>
  new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const ProfileBanner: React.FC = () => {
  const { selectedProfile, getSelectedProfiles, getSelectedName } = useProfile();

  if (!selectedProfile) return null;

  const year = new Date().getFullYear();
  const lines = getSelectedProfiles().map((p) => {
    const age = p.BirthYear ? year - p.BirthYear : '–';
    const income = p.MonthlyIncome ? `${fmtIncome(p.MonthlyIncome)}/mo` : '–';
    const other = p.OtherAnnualIncome ? ` · Other ${fmtIncome(p.OtherAnnualIncome)}/yr` : '';
    return `${p.Name} · Age ${age} · Income ${income}${other}`;
  });

  return (
    <Box
      sx={{
        p: 1.5, mb: 2, borderRadius: 1.5,
        bgcolor: 'rgba(217,119,6,0.08)',
        border: '1px solid rgba(217,119,6,0.25)',
        display: 'flex', alignItems: 'flex-start', gap: 1,
      }}
    >
      <PersonIcon sx={{ color: '#D97706', mt: 0.2, fontSize: 18 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#D97706', fontWeight: 700, display: 'block' }}>
          Profile Loaded: {getSelectedName() ?? 'Profile'}
        </Typography>
        {lines.map((line, i) => (
          <Typography key={i} variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default ProfileBanner;
